/**
 * In-place label editing: a borderless, transparent <textarea> laid exactly over
 * the element's label area, in the label's own font, size, alignment and
 * wrapping, scaled with the zoom. The rendered label is hidden meanwhile, so the
 * text never shows twice. Enter (or clicking elsewhere) commits through
 * `ttModeling.updateLabel` — one undo step; Shift+Enter inserts a line break;
 * Escape discards. Double-click any element to edit its label.
 *
 * A change of the view (a host's `setViewState` or fit, a wheel zoom or scroll) never commits: the
 * editor stays open with what was typed and follows its element, because a host may change the
 * view while the user types, and only the user's own gestures end an edit (law L5 of the
 * `@miragon/modeler-api` contract).
 *
 * An import replaces every element, so an edit open while one runs is taken down without a commit
 * and reopened, with what was typed, on the element with the same id (hosts re-import remote
 * states while the user types). Under the read-only lock no editor opens.
 */

import type Canvas from "diagram-js/lib/core/Canvas";
import type ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import type EventBus from "diagram-js/lib/core/EventBus";
import {
  isTtAnnotation,
  isTtElement,
  isTtFlow,
  isTtInteraction,
  type TtElement,
} from "../model/di-types.js";
import type TtModeling from "../modeling/TtModeling.js";
import { READ_ONLY_CHANGED_EVENT, type TtReadOnly } from "../read-only/index.js";
import { LINE_HEIGHT, labelLayout } from "../draw/label-layout.js";
import { FONT } from "../draw/styles.js";
import { isKeyOnHiddenTarget } from "../keyboard/TtKeyboard.js";

/** Canvas marker that hides the rendered label while its editor is open. */
const EDITING_MARKER = "tt-label-editing";

/** Narrow shapes (small interaction glyphs) still get an editor wide enough to type in. */
const MIN_EDITOR_WIDTH = 96;

/**
 * Enter and Escape that confirm or cancel an IME candidate belong to the composition. `isComposing`
 * alone misses Safari: it sends the confirming Enter after `compositionend`, with `isComposing`
 * false and the `keyCode` 229 that every engine gives a key the IME took.
 */
function isImeCompositionKey(event: KeyboardEvent): boolean {
  return event.isComposing || event.keyCode === 229;
}

function editorName(element: TtElement): string {
  if (isTtAnnotation(element)) return "Annotation text";
  if (isTtInteraction(element)) return "Interaction label";
  if (isTtFlow(element)) return "Flow label";
  return "Team name";
}

interface ActiveEdit {
  commit: () => void;
  cleanup: () => void;
  /** Lays the editor over its element again, after the view changed. */
  place: () => void;
  /** The element and what was typed so far, to reopen the edit after an import. */
  snapshot: () => SuspendedEdit;
}

interface SuspendedEdit {
  elementId: string;
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

export default class TtLabelEditing {
  static $inject = ["eventBus", "canvas", "ttModeling", "elementRegistry", "injector"];

  private active: ActiveEdit | null = null;
  private suspended: SuspendedEdit | null = null;
  private resumeTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly readOnly: TtReadOnly | null;

  constructor(
    eventBus: EventBus,
    private readonly canvas: Canvas,
    private readonly modeling: TtModeling,
    private readonly elementRegistry: ElementRegistry,
    injector: { get<T>(name: string, strict?: boolean): T | null },
  ) {
    this.readOnly = injector.get<TtReadOnly>("ttReadOnly", false);
    eventBus.on("element.dblclick", (event: { element?: unknown }) => {
      if (isTtElement(event.element)) this.activate(event.element);
    });
    // Any click or drag outside the editor commits (only Escape discards); a pan starts with one.
    eventBus.on(["element.mousedown", "drag.init"], () => this.active?.commit());
    eventBus.on("canvas.viewbox.changed", () => this.active?.place());
    // An import that bypasses suspend() still must not apply a pending edit to a removed element.
    eventBus.on("import.render.start", () => this.cancel());
    eventBus.on(READ_ONLY_CHANGED_EVENT, (event: { readOnly?: boolean }) => {
      if (event.readOnly) this.cancel();
    });
    // Before the canvas tears down: a field still open would blur, commit onto the destroyed
    // diagram and queue a focus on it.
    eventBus.on("diagram.destroy", () => {
      clearTimeout(this.resumeTimer);
      this.suspended = null;
      this.cancel();
    });
  }

  /**
   * Takes an open edit down WITHOUT committing it, for an import that replaces the diagram: its
   * element is about to go, so a commit would land on a removed shape. `resumeAfterImport` brings
   * the editor back with the typed text.
   */
  suspend(): void {
    clearTimeout(this.resumeTimer);
    if (!this.active) return;
    this.suspended = this.active.snapshot();
    this.active.cleanup();
  }

  /**
   * Reopens a suspended edit on the element with the same id, if the imported diagram still has
   * it. Deferred to the next task, so the host's own work around the import (restoring its view,
   * moving the focus) is done before the editor takes the focus back.
   */
  resumeAfterImport(): void {
    clearTimeout(this.resumeTimer);
    if (!this.suspended) return;
    this.resumeTimer = setTimeout(() => {
      const edit = this.suspended;
      this.suspended = null;
      if (!edit || this.active) return;
      const element = this.elementRegistry.get(edit.elementId);
      if (!isTtElement(element)) return;
      this.activate(element, edit);
    }, 0);
  }

  /** The edit open right now (its element and the uncommitted text), for hosts and tests. */
  openEdit(): { elementId: string; value: string } | undefined {
    if (!this.active) return undefined;
    const { elementId, value } = this.active.snapshot();
    return { elementId, value };
  }

  activate(element: TtElement, resumed?: SuspendedEdit): void {
    this.active?.commit();
    if (this.readOnly?.isReadOnly()) return;

    const layout = labelLayout(element);
    const centred = layout.align === "center";
    const width = centred ? Math.max(layout.box.width, MIN_EDITOR_WIDTH) : layout.box.width;
    const left = element.x + layout.box.x + (layout.box.width - width) / 2;

    const editor = document.createElement("textarea");
    editor.className = "tt-label-editor";
    editor.value = resumed ? resumed.value : (element.ttLabel ?? "");
    editor.rows = 1;
    editor.setAttribute("aria-label", editorName(element));
    Object.assign(editor.style, {
      fontFamily: FONT.family,
      fontWeight: layout.fontWeight,
      letterSpacing: layout.letterSpacing ?? "normal",
      lineHeight: String(LINE_HEIGHT),
      color: layout.fill,
      textAlign: centred ? "center" : "left",
    });

    let top = 0;
    let boxHeight = 0;
    // Grow with the text; centred labels stay vertically centred like the rendered ones.
    const fitToContent = () => {
      editor.style.height = "0px";
      const contentHeight = editor.scrollHeight;
      editor.style.height = `${centred ? contentHeight : Math.max(contentHeight, boxHeight)}px`;
      editor.style.top = `${centred ? top + (boxHeight - contentHeight) / 2 : top}px`;
    };
    const place = () => {
      const scale = this.canvas.zoom();
      const viewbox = this.canvas.viewbox();
      top = (element.y + layout.box.y - viewbox.y) * scale;
      boxHeight = layout.box.height * scale;
      Object.assign(editor.style, {
        left: `${(left - viewbox.x) * scale}px`,
        width: `${width * scale}px`,
        fontSize: `${layout.fontSize * scale}px`,
      });
      fitToContent();
    };

    this.canvas.getContainer().appendChild(editor);
    this.canvas.addMarker(element, EDITING_MARKER);
    place();
    editor.focus();
    if (resumed) editor.setSelectionRange(resumed.selectionStart, resumed.selectionEnd);
    else editor.select();

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      editor.removeEventListener("keydown", onKey);
      editor.removeEventListener("input", fitToContent);
      editor.removeEventListener("blur", onBlur);
      editor.remove();
      if (this.elementRegistry.get(element.id) === element) {
        this.canvas.removeMarker(element, EDITING_MARKER);
      }
      this.active = null;
      // Removing the focused field drops the focus to the page; the canvas takes it back.
      this.canvas.restoreFocus();
    };
    const commit = () => {
      if (done) return;
      const value = editor.value.trim();
      const changed = value !== (element.ttLabel ?? "");
      cleanup();
      if (changed) this.modeling.updateLabel(element, value);
    };
    // Removing the focused editor would drop focus to the page; keep keyboard users on the canvas.
    const onKey = (e: KeyboardEvent) => {
      if (isImeCompositionKey(e) || isKeyOnHiddenTarget(e)) return;
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        commit();
        this.canvas.focus();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cleanup();
        this.canvas.focus();
      }
    };
    const onBlur = () => commit();
    editor.addEventListener("keydown", onKey);
    editor.addEventListener("input", fitToContent);
    editor.addEventListener("blur", onBlur);

    const snapshot = (): SuspendedEdit => ({
      elementId: element.id,
      value: editor.value,
      selectionStart: editor.selectionStart,
      selectionEnd: editor.selectionEnd,
    });
    this.active = { commit, cleanup, place, snapshot };
  }

  cancel(): void {
    this.active?.cleanup();
  }
}
